import React, {useEffect, useState} from "react";
import type {ExternalCompletion as Completion} from "../generated/openapi-contract";
import {ApiError, apiFetch} from "../api/client";
import {formatDate} from "../utils";

export interface ExternalCompletionEvent {
    id: string;
    attempt: number;
    event: string;
    outcome: string;
    externalStatus?: string;
    reasonCode?: string;
    errorCode?: string;
    httpStatus?: number;
    at: string;
}

interface CompletionResponse {
    completion: Completion | null;
    history: ExternalCompletionEvent[];
}

export function completionPending(completion?: Completion | null): boolean {
    return completion?.state === "pending" || completion?.state === "awaiting_confirmation";
}

// Чек не принят: отмена не отправлена и ждёт, пока трейдер отменит заявку руками. Состояние
// attention_required конечное, но снять пометку может сам трейдер (карточка станет cancelled) — поэтому
// статус перечитываем и тут, только реже.
export function completionProofRejected(completion?: Completion | null): boolean {
    return completion?.action === "cancelled" && completion.proofRejected === true && completion.state !== "confirmed";
}

// Событий о смене попытки или статуса команды по WebSocket нет (он публикует только свободный пул), поэтому
// пока команда в работе, статус опрашивается. Попытки чека идут на 0/5/15/25 с: 3 с ловят каждую.
export const EXTERNAL_COMPLETION_POLL_MS = 3000;
export const EXTERNAL_COMPLETION_HELD_POLL_MS = 15000;

// После быстрых проверок завершение ждёт Paylord до часа и читает карточку раз в 2 минуты: если
// следующая проверка не скоро, частый опрос ничего не покажет.
const COMPLETION_WATCH_AHEAD_MS = 10000;

// null — опрашивать нечего: статус конечный.
export function completionPollMs(completion?: Completion | null, now: number = Date.now()): number | null {
    if (completionPending(completion)) {
        const nextCheckAt = completion?.nextCheckAt ? Date.parse(completion.nextCheckAt) : NaN;
        const watching = !completionIsCancel(completion) && nextCheckAt - now > COMPLETION_WATCH_AHEAD_MS;
        return watching ? EXTERNAL_COMPLETION_HELD_POLL_MS : EXTERNAL_COMPLETION_POLL_MS;
    }
    if (completionProofRejected(completion)) return EXTERNAL_COMPLETION_HELD_POLL_MS;
    return null;
}

// Завершение остановлено на проверку: Paylord не подтвердил его за час или ответил не то. Это дело
// админа — трейдер своё сделал, а админ сверяется с Paylord и снимает проверку.
export function completionAwaitsAdmin(completion?: Completion | null): boolean {
    return completion != null && !completionIsCancel(completion) && completion.state === "attention_required";
}

// Самый частый из нужных интервалов по списку платежей; null, если опрашивать нечего.
export function completionListPollMs(completions: Array<Completion | null | undefined>): number | null {
    const intervals = completions.map(completion => completionPollMs(completion)).filter((ms): ms is number => ms !== null);
    return intervals.length ? Math.min(...intervals) : null;
}

// Бейдж один на две команды площадке. Отмена подписана по-русски и словом
// «отправляется»: трейдеру надо видеть, что отмена уже в пути и повторно
// нажимать нечего. Подписи завершения не менялись.
export function completionIsCancel(completion?: Completion | null): boolean {
    return completion?.action === "cancelled";
}

// Ход загрузки чека к отмене: бэкенд отдаёт попытки, пока чек ещё уходит. Потом подпись возвращается
// к «Отмена → Paylord: отправляется».
export function completionProofProgress(completion?: Completion | null): string | null {
    if (!completionIsCancel(completion) || !completionPending(completion) || completion?.proofRejected) return null;
    if (completion?.proofAttempts == null || completion.proofMaxAttempts == null) return null;
    return completion.proofAttempts > 0
        ? `Отправляем чек в Paylord: попытка ${completion.proofAttempts} из ${completion.proofMaxAttempts}`
        : "Отправляем чек в Paylord";
}

export function completionLabel(completion: Completion): string {
    if (completionIsCancel(completion)) {
        if (completionProofRejected(completion)) return "Отмена → Paylord: отмените вручную";
        const progress = completionProofProgress(completion);
        if (progress) return progress;
        switch (completion.state) {
            case "confirmed": return "Отмена → Paylord: подтверждена";
            case "attention_required": return "Отмена → Paylord: нужно внимание";
            default: return "Отмена → Paylord: отправляется";
        }
    }
    switch (completion.state) {
        case "confirmed": return "Paylord: Confirmed";
        case "attention_required": return "Paylord: Needs review";
        default: return "Paylord: Awaiting confirmation";
    }
}

// Чек к отмене Paylord не принял, и отмена туда не отправлена: трейдер отменяет заявку сам.
export function completionNote(completion?: Completion | null): string | null {
    if (!completionProofRejected(completion)) return null;
    const attempts = completion?.proofAttempts ?? 0;
    const after = attempts > 0 ? ` после ${attempts} ${attempts === 1 ? "попытки" : "попыток"}` : "";
    return `Paylord не принял чек${after} — отмена не отправлена. Отмените заявку вручную в Paylord.`;
}

export function ExternalCompletionBadge({completion}: {completion?: Completion | null}): JSX.Element | null {
    if (!completion) return null;
    const color = completion.state === "confirmed" ? "success"
        : completionProofRejected(completion) ? "failed"
            : completion.state === "attention_required" ? "appeal" : "pending";
    const what = completionIsCancel(completion) ? "Отправки отмены" : "Отправки завершения";
    return <span className={`payment-status status-${color}`} title={`${what}: ${completion.attempts}/${completion.maxAttempts}`}>
        {completionLabel(completion)}
    </span>;
}

export function ExternalCompletionNote({completion}: {completion?: Completion | null}): JSX.Element | null {
    const note = completionNote(completion);
    return note ? <span className="payment-bank payment-note-error">{note}</span> : null;
}

const eventLabels: Record<string, string> = {
    preflight_started: "Проверка перед отправкой начата",
    preflight_result: "Результат проверки перед отправкой",
    mutation_started: "Отправка завершения начата",
    mutation_result: "Результат отправки",
    confirmation_started: "Проверка статуса начата",
    confirmation_result: "Результат проверки",
    retry_scheduled: "Повторная отправка запланирована",
    confirmed: "Подтверждение получено",
    blocked: "Требуется проверка администратора",
};

const cancelEventLabels: Record<string, string> = {
    mutation_started: "Отправка отмены начата",
    retry_scheduled: "Повторная отправка отмены запланирована",
};

const reasonLabels: Record<string, string> = {
    completed_confirmation_exhausted: "Попытки отправки исчерпаны",
    completed_fact_sum_mismatch: "Сумма в Paylord отличается от суммы завершения",
    ownership_mismatch: "Владелец заявки изменился",
    identity_mismatch: "Данные заявки не совпадают",
    external_status_conflict: "Статус Paylord не позволяет завершение",
    authorization_rejected: "Paylord отклонил доступ",
    completion_evidence_missing: "Нет достаточных данных подтверждения",
    mutation_rejected: "Paylord отклонил завершение",
    confirmation_unknown: "Подтверждение не получено",
    precompleted_wait_exhausted: "Paylord не завершил обработку вовремя",
    delivery_blocked: "Отправка требует проверки",
    review_required: "Требуется проверка",
};

const errorLabels: Record<string, string> = {
    network: "Сетевая ошибка", server: "Ошибка сервера Paylord",
    contract: "Ответ Paylord не прошёл проверку", unauthorized: "Требуется восстановление доступа",
    forbidden: "Доступ отклонён", validation: "Paylord отклонил данные",
    unavailable: "Paylord недоступен", rejected: "Запрос отклонён", unknown: "Результат неизвестен",
};

const outcomeLabels: Record<string, string> = {
    success: "успешно", accepted: "принято", confirmed: "подтверждено",
    pending: "ожидается", started: "начато", scheduled: "запланировано",
    observed: "статус прочитан", blocked: "нужна проверка",
    error: "ошибка", failed: "ошибка", rejected: "отклонено", unknown: "результат неизвестен",
};

// canConfirm — только у админа: кнопка снимает проверку, когда он сверился с Paylord.
export function ExternalCompletionDetails({paymentId, completion, token, canConfirm = false}: {
    paymentId: string;
    completion?: Completion | null;
    token: string | null;
    canConfirm?: boolean;
}): JSX.Element | null {
    const [snapshot, setSnapshot] = useState<CompletionResponse | null>(null);
    const [error, setError] = useState(false);
    const [armed, setArmed] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [confirmError, setConfirmError] = useState<string | null>(null);
    const hasCompletion = completion != null;
    const completionState = completion?.state;
    useEffect(() => {
        setSnapshot(null);
        setError(false);
        if (!token || !hasCompletion) return;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const refresh = async () => {
            try {
                const next = await apiFetch<CompletionResponse>(`/payments/${paymentId}/external-completion`, token);
                if (cancelled) return;
                setSnapshot(next);
                setError(false);
                const delay = completionPollMs(next.completion);
                if (delay !== null) timer = setTimeout(refresh, delay);
            } catch {
                if (cancelled) return;
                setError(true);
                timer = setTimeout(refresh, EXTERNAL_COMPLETION_POLL_MS);
            }
        };
        void refresh();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [paymentId, token, hasCompletion, completionState]);
    const current = snapshot?.completion ?? completion;
    if (!current) return null;
    const confirmCompletion = async () => {
        setConfirming(true);
        setConfirmError(null);
        try {
            const next = await apiFetch<CompletionResponse>(`/payments/${paymentId}/external-completion/confirm`, token, {method: "POST"});
            setSnapshot(next);
            setArmed(false);
        } catch (err) {
            setConfirmError(err instanceof ApiError && err.status === 409
                ? "Проверка уже снята или больше не нужна. Обновите страницу."
                : "Не удалось снять проверку. Попробуйте ещё раз.");
        } finally {
            setConfirming(false);
        }
    };
    const cancel = completionIsCancel(current);
    const note = completionNote(current);
    const rejected = completionProofRejected(current);
    return <section className="details-section" aria-label="Подтверждение Paylord">
        <ExternalCompletionBadge completion={current}/>
        <p>{cancel ? "Отправки отмены" : "Отправки завершения"}: {current.attempts}/{current.maxAttempts}.</p>
        {completionPending(current) && <p>{cancel
            ? "Paylord ещё не подтвердил отмену. Статус проверяется автоматически, повторно нажимать «Отмена» не нужно."
            : "Paylord ещё не подтвердил завершение. Статус проверяется автоматически."}</p>}
        {note && <p>{note}</p>}
        {rejected && <p>Как только заявка будет отменена в Paylord, статус обновится сам.</p>}
        {!rejected && current.state === "attention_required" && <p>Подтверждение Paylord требует проверки администратора.</p>}
        {!rejected && current.reasonCode && <p>{reasonLabels[current.reasonCode] ?? "Требуется проверка подтверждения"}</p>}
        {canConfirm && token && completionAwaitsAdmin(current) && (armed
            ? <p>Проверьте в Paylord, что заявка завершена с той же суммой.{" "}
                <button className="btn btn-success btn-sm" type="button" disabled={confirming} onClick={() => void confirmCompletion()}>Снять проверку</button>{" "}
                <button className="btn btn-ghost btn-sm" type="button" disabled={confirming} onClick={() => setArmed(false)}>Отмена</button>
            </p>
            : <p><button className="btn btn-success btn-sm" type="button" onClick={() => setArmed(true)}>Paylord подтвердил — снять проверку</button></p>)}
        {confirmError && <p role="alert">{confirmError}</p>}
        {current.nextCheckAt && <p>Следующая проверка: {formatDate(current.nextCheckAt)}</p>}
        {current.confirmedAt && <p>Подтверждено: {formatDate(current.confirmedAt)}</p>}
        {error && <p role="status">Не удалось обновить подтверждение Paylord. Повторяем проверку.</p>}
        {snapshot?.history.length ? <details>
            <summary>История отправки и проверки ({snapshot.history.length})</summary>
            <div className="activity-table-wrap"><table className="activity-table">
                <thead><tr><th>Время</th><th>Попытка</th><th>Событие</th><th>Результат</th></tr></thead>
                <tbody>{snapshot.history.map(event => <tr key={event.id}>
                    <td>{formatDate(event.at)}</td><td>{event.attempt || "—"}</td>
                    <td>{(cancel ? cancelEventLabels[event.event] : undefined) ?? eventLabels[event.event] ?? (cancel ? "Проверка отмены" : "Проверка завершения")}</td>
                    <td>{outcomeLabels[event.outcome] ?? "зафиксировано"}{event.externalStatus ? ` · ${event.externalStatus}` : ""}{event.reasonCode ? ` · ${reasonLabels[event.reasonCode] ?? "требуется проверка"}` : ""}{event.errorCode ? ` · ${errorLabels[event.errorCode] ?? "ошибка проверки"}` : ""}{event.httpStatus ? ` · HTTP ${event.httpStatus}` : ""}</td>
                </tr>)}</tbody>
            </table></div>
        </details> : null}
    </section>;
}
